/**
 * The room count control that replaces "Add room" once a room is on the trip: a minus / count / plus
 * picker with Book (go to checkout for the added rooms) or Booked (open the booking) beside it.
 *
 * Shared by web and mobile so both apps agree on what the traveler sees for the same items:
 * - none: no room of this rate on the trip — a single "Add room" button.
 * - added: N planned rooms — the picker reads "N added", with Book.
 * - booked: N booked rooms and nothing planned — the picker reads "N booked" with minus disabled
 *   (a booked room is cancelled from its booking, not stepped down), and a green Booked button.
 * - bookedAndAdded: rooms added after an earlier booking — the picker reads the added count with Book, and
 *   the earlier bookings are not shown on the card.
 *
 * Cancelled rooms are neither added nor booked: they were given back.
 */

import type { ItineraryItem } from '#models/ItineraryItemModel.js';
import type { RoomOptionModel } from '#models/RoomOptionModel.js';
import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import { occupancyKey } from './occupancy-utils.js';
import { ROOM_QUANTITY_MAX } from './room-utils.js';

/** A step on the picker waiting on the server: plus checks the hotel and adds a room, minus removes one. */
export type RoomCountStep = 'increment' | 'decrement';

/** The fields of an itinerary item the picker reads. */
export type RoomCountItem = Pick<
  ItineraryItem,
  'id' | 'isPlanned' | 'isBooked' | 'isApproved' | 'isCancellationPending' | 'isCancelled'
> & { createdAt?: Date | string | null };

interface CountControls {
  /** Whether plus may add another room: the rate is still bookable and the add cap is not reached. */
  canIncrement: boolean;
  /** Whether minus may remove a room: only planned rooms can be stepped down. */
  canDecrement: boolean;
}

export type RoomCountPickerState<T extends RoomCountItem = RoomCountItem> =
  | ({ kind: 'none' } & CountControls)
  | ({ kind: 'added'; added: number } & CountControls)
  | ({ kind: 'booked'; booked: number; bookedItems: T[] } & CountControls)
  | ({ kind: 'bookedAndAdded'; added: number; booked: number; bookedItems: T[] } & CountControls);

/** Booked, approved or awaiting cancellation: the hotel holds the room for the traveler. */
export function isCommittedRoom(item: RoomCountItem): boolean {
  return item.isBooked || item.isApproved || item.isCancellationPending;
}

/** A room on the trip that has not been booked: what minus removes and Book sends to checkout. */
export function isAddedRoom(item: RoomCountItem): boolean {
  return item.isPlanned && !item.isCancelled;
}

/**
 * The picker state for the rooms of one rate (or one room) on the trip.
 *
 * @param cap the most planned rooms plus may reach; the add flow accepts at most `ROOM_QUANTITY_MAX`.
 * @param isAvailable false when the rate is known to be gone: plus is disabled, minus still works.
 */
export function roomCountPickerState<T extends RoomCountItem>(
  items: readonly T[],
  { cap = ROOM_QUANTITY_MAX, isAvailable = true }: { cap?: number; isAvailable?: boolean } = {},
): RoomCountPickerState<T> {
  const added = items.filter(isAddedRoom).length;
  const bookedItems = items.filter(isCommittedRoom);
  const booked = bookedItems.length;
  const controls: CountControls = { canIncrement: isAvailable && added < cap, canDecrement: added > 0 };

  if (booked > 0 && added > 0) return { kind: 'bookedAndAdded', added, booked, bookedItems, ...controls };
  if (booked > 0) return { kind: 'booked', booked, bookedItems, ...controls };
  if (added > 0) return { kind: 'added', added, ...controls };
  return { kind: 'none', ...controls };
}

/**
 * Whether the trip already holds as many added rooms of this room as the hotel says it has: the rate's
 * `availableQuantity` from the search, a recheck, or an add the hotel refused (the "N rooms left" the guide
 * shows), or the room's when no rate is named. Counts added rooms at every rate and party, as the hotel's check does, so plus stops before
 * the hotel has to refuse. False when no count is stated.
 *
 * @param roomItems every itinerary item of the room, at any rate.
 */
export function isAtStatedRoomLimit(
  room: Pick<RoomOptionModel, 'getRateByIdentity' | 'maxAvailableQuantity'>,
  identity: { productCode?: string; occupancy?: Occupancy },
  roomItems: readonly RoomCountItem[],
): boolean {
  const stated = identity.productCode
    ? room.getRateByIdentity({ productCode: identity.productCode, occupancy: identity.occupancy })?.availableQuantity
    : room.maxAvailableQuantity;
  return stated != null && roomItems.filter(isAddedRoom).length >= stated;
}

/** The count the picker shows: the added rooms, or the booked ones when nothing is added since. */
export function roomCountLabel(state: RoomCountPickerState): string {
  switch (state.kind) {
    case 'none':
      return '';
    case 'booked':
      return `${state.booked} booked`;
    case 'added':
    case 'bookedAndAdded':
      return `${state.added} added`;
  }
}

/** The green button once every added room is booked: "Booked", with the count when there are several. */
export function bookedButtonLabel(booked: number): string {
  return booked > 1 ? `${booked} booked` : 'Booked';
}

/**
 * The planned room minus removes: the newest, so the rooms the traveler added first (and may already
 * have shared or annotated) stay. Undefined when nothing is planned.
 */
export function newestAddedRoom<T extends RoomCountItem>(items: readonly T[]): T | undefined {
  const time = (item: RoomCountItem) => {
    const ms = item.createdAt ? new Date(item.createdAt).getTime() : Number.NaN;
    return Number.isNaN(ms) ? 0 : ms;
  };
  // Items arrive oldest first, so on a tie (or no timestamps) the later one is the newer.
  let newest: T | undefined;
  for (const item of items) {
    if (isAddedRoom(item) && (!newest || time(item) >= time(newest))) newest = item;
  }
  return newest;
}

/**
 * The key the store files a picker's busy flag and inventory message under: one per room and rate
 * identity, so two rate cards of the same room never share a spinner.
 */
export function roomCountKey(roomId: string, identityMeta: Record<string, unknown> | undefined): string {
  const productCode = typeof identityMeta?.productCode === 'string' ? identityMeta.productCode : '*';
  const occupancy = identityMeta?.occupancy as Parameters<typeof occupancyKey>[0] | undefined;
  return `${roomId}|${productCode}|${occupancy ? occupancyKey(occupancy) : '*'}`;
}
